import React, { memo, useCallback, useMemo } from 'react';

import { Box, InputAdornment } from '@mui/material';
import PropTypes from 'prop-types';

import {
  ALL_ICON_OPTIONS,
  findIconOptionByValue,
  getIconComponentByValue,
} from 'constants/iconOptions';

import AutocompleteCDS from '../AutocompleteCDS';

import { optionContentStyles, startAdornmentStyles } from './IconPickerCDS.styles';

const iconOptionsByName = new Map(ALL_ICON_OPTIONS.map(opt => [opt.name, opt]));

const IconOptionContent = ({ optionText }) => {
  const iconOption = iconOptionsByName.get(optionText);
  const IconComponent = iconOption ? getIconComponentByValue(iconOption.value) : null;

  return (
    <Box sx={optionContentStyles}>
      {IconComponent && <IconComponent />}
      <span>{optionText}</span>
    </Box>
  );
};

IconOptionContent.propTypes = {
  optionText: PropTypes.string,
};

const getOptionLabel = option => option?.name || '';

const IconPickerCDS = ({
  name = '',
  label = '',
  value = '',
  onChange,
  options = null,
  disabled = false,
  error = false,
  helperText = '',
  required = false,
  placeholder = '',
  className = '',
  containerClassName = '',
  dataTestId = '',
}) => {
  const selectedOption = useMemo(() => findIconOptionByValue(value), [value]);

  const SelectedIconComponent = useMemo(() => getIconComponentByValue(value), [value]);

  const inputProps = useMemo(
    () =>
      SelectedIconComponent
        ? {
            InputProps: {
              startAdornment: (
                <InputAdornment position="start" sx={startAdornmentStyles}>
                  <SelectedIconComponent />
                </InputAdornment>
              ),
            },
          }
        : {},
    [SelectedIconComponent],
  );

  const onAutocompleteChange = useCallback(
    (__, selectedValue) => {
      onChange({ target: { name, value: selectedValue?.value || '' } });
    },
    [name, onChange],
  );

  return (
    <AutocompleteCDS
      OptionContent={IconOptionContent}
      className={className}
      containerClassName={containerClassName}
      dataTestId={dataTestId}
      disabled={disabled}
      error={error}
      getOptionLabel={getOptionLabel}
      helperText={helperText}
      inputProps={inputProps}
      label={label}
      name={name}
      options={options || ALL_ICON_OPTIONS}
      placeholder={placeholder}
      required={required}
      value={selectedOption}
      onChange={onAutocompleteChange}
    />
  );
};

IconPickerCDS.propTypes = {
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  containerClassName: PropTypes.string,
  dataTestId: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  options: PropTypes.array,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.string,
};

export default memo(IconPickerCDS);
